import { API_CONFIG } from '../config/api';
import { ApiValidationError } from '../types/auth';
import type {
  LoginCredentials,
  PlatformAuthUser,
  PlatformLoginResponse,
  PlatformMeResponse,
} from '../types/platformAdmin';

const PLATFORM_STORAGE_KEYS = ['platform_token', 'platform_user', 'platform_user_name'] as const;

export const storePlatformSession = (token: string, user: PlatformAuthUser) => {
  localStorage.setItem('platform_token', token);
  localStorage.setItem('platform_user', JSON.stringify(user));
  localStorage.setItem('platform_user_name', user.name);
};

export const clearPlatformSession = () => {
  PLATFORM_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
};

export const isPlatformAuthenticated = () => {
  const token = localStorage.getItem('platform_token');
  const user = localStorage.getItem('platform_user');
  return Boolean(token && user);
};

export const getPlatformUser = (): PlatformAuthUser | null => {
  try {
    const raw = localStorage.getItem('platform_user');
    return raw ? (JSON.parse(raw) as PlatformAuthUser) : null;
  } catch {
    return null;
  }
};

const parseErrorResponse = async (response: Response) => {
  const errorData = await response.json().catch(() => ({}));
  const message = errorData.message || `Request failed (${response.status})`;

  if (response.status === 422 && errorData.errors) {
    throw new ApiValidationError(message, errorData.errors);
  }

  throw new Error(message);
};

export const platformLogin = async (
  credentials: LoginCredentials
): Promise<PlatformLoginResponse> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH.LOGIN}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    await parseErrorResponse(response);
  }

  const result: PlatformLoginResponse = await response.json();

  if (!result.user.is_platform_admin) {
    throw new Error('This account is not authorized for platform administration.');
  }

  return result;
};

export const getPlatformMe = async (): Promise<PlatformAuthUser> => {
  const token = localStorage.getItem('platform_token');

  if (!token) {
    throw new Error('No authentication token');
  }

  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH.ME}`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    await parseErrorResponse(response);
  }

  const result: PlatformMeResponse = await response.json();

  if (!result.data.is_platform_admin) {
    throw new Error('This account is not authorized for platform administration.');
  }

  return result.data;
};

export const restorePlatformSession = async (): Promise<PlatformAuthUser | null> => {
  if (!localStorage.getItem('platform_token')) {
    return null;
  }

  try {
    const user = await getPlatformMe();
    storePlatformSession(localStorage.getItem('platform_token')!, user);
    return user;
  } catch {
    clearPlatformSession();
    return null;
  }
};

export const platformLogout = async () => {
  try {
    const token = localStorage.getItem('platform_token');

    if (token) {
      await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH.LOGOUT}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });
    }
  } catch (error) {
    console.error('Platform logout API error:', error);
  } finally {
    clearPlatformSession();
    window.location.href = '/platform-admin/login';
  }
};
